import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { SessionState, SignOut } from '@platform-frontend/account';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { InvalidStateError } from '@platform-frontend/util';
import { filter, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { StartupTempDataStateModel } from './model/startup-temp-data-state-model';
import { PersistAndCleanupStartupTempData, SetStartupTempData } from './startup-temp-data.actions';

const DEFAULT_STATE: StartupTempDataStateModel = {
  metadataWithId: null,
  forceSignIn: false
};

@State<StartupTempDataStateModel>({
  name: 'startupTempData',
  defaults: DEFAULT_STATE
})
@Injectable()
export class StartupTempDataState {
  constructor(private http: HttpClient, private store: Store) {}

  @Action(SetStartupTempData)
  setMetadata(ctx: StateContext<StartupTempDataStateModel>, { metadataWithId, forceSignIn }: SetStartupTempData): void {
    ctx.patchState({ metadataWithId, forceSignIn });
  }

  @Action(PersistAndCleanupStartupTempData)
  persistAndCleanupStartupTempData(ctx: StateContext<StartupTempDataStateModel>, { metadataId }: PersistAndCleanupStartupTempData): Observable<unknown> {
    return this.signOutIfForceSignIn(ctx).pipe(
      filter(forcedSignIn => !forcedSignIn),
      switchMap(() => this.persistMetadata(ctx, metadataId))
    );
  }

  private signOutIfForceSignIn(ctx: StateContext<StartupTempDataStateModel>): Observable<boolean> {
    const forceSignIn = ctx.getState().forceSignIn;
    const isSignedIn = this.store.selectSnapshot(SessionState.isSignedIn);
    const handleSignOut$ = forceSignIn && isSignedIn ? this.store.dispatch(new SignOut('authenticate')).pipe(map(() => true)) : of(false);
    return handleSignOut$.pipe(tap(() => ctx.patchState({ forceSignIn: false })));
  }

  private persistMetadata(ctx: StateContext<StartupTempDataStateModel>, metadataId: string | null): Observable<unknown> {
    const metadataWithId = ctx.getState().metadataWithId;
    if (!metadataWithId || !metadataId) {
      return of(null);
    } else if (metadataWithId.id !== metadataId) {
      return throwError(() => new InvalidStateError('Metadata ID mismatch'));
    }

    const handleMetaData$: Observable<unknown> =
      metadataWithId && metadataWithId.metadata !== null
        ? this.http.post(PLATFORM_ENVIRONMENT.apiUrl + '/metadata', { metadata: metadataWithId.metadata, metadataId })
        : of(null);
    return handleMetaData$.pipe(tap(() => ctx.patchState({ metadataWithId: null })));
  }
}
